import { useTimeout } from "@fuse/hooks";
import { useEffect, useState } from "react";
import clsx from "clsx";
import { createPortal } from "react-dom";

const FROM_OPTIONS = ["analise-estoque"] as const;

export type FuseLoadingProgresso = {
	/** Percentual concluído (0 a 100). */
	readonly percentual: number;
	readonly mensagem?: string;
};

export interface FuseLoadingProps {
	delay?: number;
	message?: string;
	// message list
	messages?: string[];
	messageIntervalMs?: number;
	isFrom?: (typeof FROM_OPTIONS)[number];
	/** Quando informado, exibe uma barra determinada e substitui a rotação de mensagens. */
	progresso?: FuseLoadingProgresso;
}

const MESSAGE_FADE_DURATION_MS = 260;

/**
 * FuseLoading displays a loading state with an optional delay
 */
function FuseLoading(props: FuseLoadingProps) {
	const { delay = 0, message = "", messages, messageIntervalMs = 4200, isFrom, progresso } = props;
	const [showLoading, setShowLoading] = useState(!delay);
	const [messageIndex, setMessageIndex] = useState(0);
	const [isMessageFading, setIsMessageFading] = useState(false);
	const [elapsedSeconds, setElapsedSeconds] = useState(0);

	const effectiveMessages = (() => {
		const list = (messages ?? []).filter((m) => Boolean(String(m ?? "").trim()));
		if (list.length > 0) return list;
		const single = String(message ?? "").trim();
		return single ? [single] : [];
	})();

	// A mensagem do servidor ganha destaque; as mensagens padrão seguem girando abaixo dela.
	const percentualProgresso = progresso ? Math.min(100, Math.max(0, progresso.percentual)) : null;
	const mensagemProgresso = progresso?.mensagem?.trim() ?? "";
	const currentMessage = effectiveMessages.length ? effectiveMessages[messageIndex % effectiveMessages.length] : "";

	useTimeout(() => {
		setShowLoading(true);
	}, delay);

	// --------------------- TIMER PARA ANALISE DE ESTOQUE ---------------------
	useEffect(() => {
		if (!showLoading || isFrom !== "analise-estoque") return;
		setElapsedSeconds(0);
		const id = window.setInterval(() => {
			setElapsedSeconds((s) => s + 1);
		}, 1000);
		return () => window.clearInterval(id);
	}, [showLoading, isFrom]);

	useEffect(() => {
		// reset index when message list changes
		setMessageIndex(0);
		setIsMessageFading(false);
	}, [effectiveMessages.length]);

	useEffect(() => {
		if (!showLoading) return;
		if (effectiveMessages.length <= 1) return;
		if (!Number.isFinite(messageIntervalMs) || messageIntervalMs <= 0) return;

		const timeoutIds: number[] = [];

		const id = window.setInterval(() => {
			setIsMessageFading(true);

			const timeoutId = window.setTimeout(() => {
				setMessageIndex((i) => (i + 1) % effectiveMessages.length);
				setIsMessageFading(false);
			}, MESSAGE_FADE_DURATION_MS);

			timeoutIds.push(timeoutId);
		}, messageIntervalMs);

		return () => {
			window.clearInterval(id);
			timeoutIds.forEach((timeoutId) => window.clearTimeout(timeoutId));
		};
		// `progresso` fica fora das dependências de propósito: cada evento do WebSocket cria um objeto
		// novo e reiniciaria o intervalo, travando a rotação das mensagens padrão.
	}, [showLoading, effectiveMessages.length, messageIntervalMs]);

	if (!showLoading) return null;

	return createPortal(
		<div
			className={clsx(
				"pointer-events-auto fixed inset-0 z-[10000] flex items-center justify-center bg-[#eef2f7]/95 px-4",
			)}
			onClick={(e) => {
				e.preventDefault();
				e.stopPropagation();
			}}
		>
			<div
				className={clsx(
					"animate-space-loading-card-in relative flex w-full max-w-[380px] flex-col items-center overflow-hidden rounded-[24px] bg-white px-14 pt-12 pb-11",
					"shadow-[0_0_0_1px_rgba(0,55,112,0.07),0_24px_64px_rgba(0,55,112,0.13),0_4px_16px_rgba(0,0,0,0.05)]",
				)}
			>
				<div className="absolute inset-x-0 top-0 h-[3px] bg-[linear-gradient(90deg,#e6215a,#504a99,#76b0bf)]" />
				<div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-10%,rgba(80,74,153,0.04),transparent)]" />

				<div className="relative z-10 mb-9">
					<img
						src="/assets/space/logo_space_blue.png"
						alt="Space Software"
						className="h-10 w-auto object-contain"
					/>
				</div>

				<div className="relative z-10 mb-8 h-[72px] w-[72px] shrink-0">
					<div className="absolute inset-0 rounded-full border-2 border-[rgba(80,74,153,0.1)]" />
					<div className="animate-space-loading-spin absolute inset-0 rounded-full border-2 border-transparent border-t-[#e6215a] border-r-[#504a99]" />
					<div className="animate-space-loading-spin-reverse absolute inset-[10px] rounded-full border-2 border-transparent border-b-[#76b0bf] border-l-[#67a0d6]" />
					<div className="absolute inset-0 flex items-center justify-center">
						<div className="animate-space-loading-pulse h-2 w-2 rounded-full bg-[linear-gradient(135deg,#e6215a,#504a99)] shadow-[0_0_10px_rgba(230,33,90,0.5)]" />
					</div>
				</div>

				<div>
					{isFrom === "analise-estoque" && (
						<div className="mb-1.5 font-bold tracking-[-0.01em] text-[#003770]">
							<span className="ml-2 font-mono text-md font-normal text-[#85a3c2]">
								{String(Math.floor(elapsedSeconds / 60)).padStart(2, "0")}:
								{String(elapsedSeconds % 60).padStart(2, "0")}
							</span>
						</div>
					)}
				</div>

				<div className="relative z-10 mx-auto text-center">
					{!mensagemProgresso && (
						<div className="mb-4 text-[15px] font-bold tracking-[-0.01em] text-[#003770]">
							Carregando dados...
						</div>
					)}

					{mensagemProgresso && (
						<div className="mx-auto mb-1 text-md leading-snug font-semibold text-[#173f7a]">
							{mensagemProgresso}
						</div>
					)}

					{currentMessage && (
						<div
							className={clsx(
								"mx-auto min-h-[18px] font-mono tracking-[0.04em] transition-opacity duration-[250ms]",
								mensagemProgresso ? "text-xs text-[#a8b6c8]" : "text-xs text-[#85a3c2]",
								isMessageFading ? "opacity-0" : "opacity-100",
							)}
						>
							{currentMessage}
						</div>
					)}
				</div>

				<div className="relative z-10 mt-7 w-full">
					<div className="h-[3px] w-full overflow-hidden rounded-full bg-[#edf1f7]">
						{percentualProgresso === null ? (
							<div className="animate-space-loading-shimmer h-full w-full rounded-full bg-[linear-gradient(90deg,#e6215a,#504a99,#76b0bf,#e6215a)] [background-size:300%_100%]" />
						) : (
							<div
								className="h-full rounded-full bg-[linear-gradient(90deg,#e6215a,#504a99,#76b0bf)] transition-[width] duration-300 ease-out"
								style={{ width: `${percentualProgresso}%` }}
								role="progressbar"
								aria-valuenow={percentualProgresso}
								aria-valuemin={0}
								aria-valuemax={100}
							/>
						)}
					</div>

					{percentualProgresso !== null && (
						<div className="mt-2 text-right font-mono text-[11px] font-semibold tracking-[0.04em] text-[#003770]">
							{percentualProgresso}%
						</div>
					)}
				</div>

				<div className="relative z-10 mt-[18px] flex gap-[5px]">
					<div className="animate-space-loading-dot-pulse h-1 w-1 rounded-full bg-[#dde4ec]" />
					<div className="animate-space-loading-dot-pulse h-1 w-1 rounded-full bg-[#dde4ec] [animation-delay:0.2s]" />
					<div className="animate-space-loading-dot-pulse h-1 w-1 rounded-full bg-[#dde4ec] [animation-delay:0.4s]" />
				</div>
			</div>
		</div>,
		document.body,
	);
}

export default FuseLoading;
